/* eslint-disable react/forbid-prop-types */
import React, { Fragment, useCallback, useMemo } from 'react';
import Icon, { ICON_TYPE_MINUS, ICON_TYPE_PLUS } from '../../Icon';
import { getClassNames } from '../../../utils';
import { RowData } from './Row';
import { Column } from '../utils/types';

type IColumn = Column & {
    selection?: (data: any, row: RowData) => any;
    onCell?: (row: RowData, index: number) => any;
    titleType?: string;
};

interface IRowCellProps {
    row: RowData;
    data: any,
    level: number,
    index: number,
    column: IColumn;
    expanded: boolean,
    scrollable: boolean,
    onExpand: () => void,
};

function RowCell(props: IRowCellProps) {
    const {
        row,
        data,
        index,
        level,
        column: {
            hidden,
            render,
            sorter,
            sortable,
            className,
            selection,
            filterable,
            titleType,
            onCell,
        },
        expanded,
        onExpand,
    } = props;

    if (hidden) return null;

    const cellPops = typeof onCell === 'function' ? onCell(row, index) : {};
    const selectComponent = useMemo(() => (typeof selection === 'function' ? selection(data, row) : null),
        [selection, data, row]);
    const expandIcon = useMemo(() => index === 0 && row.children && row.children.length > 0,
        [row, index]);
    const expandRender = useCallback(
        (render) => (level > 0 && index === 0 ? <div className="expand-cell">{render}</div> : render),
        [level, index],
    );
    const classNameCell = getClassNames(className,
        { 'cell-sortable': sortable || sorter },
        { 'cell-filterable': filterable },
        { 'cell-expandable': expandIcon },
        { 'cell-fixed': titleType === 'columns' });
    const InnerComponent = useMemo(() => (selectComponent ? 'div' : Fragment),
        [selectComponent]);
    const innerComponentProps = useMemo(() => (selectComponent ? { className: 'cell-row-selection' } : {}),
        [selectComponent]);

    return (
        <td
            role="cell"
            className={classNameCell}
            {...cellPops}
        >
            <InnerComponent {...innerComponentProps}>
                {selectComponent}
                {expandIcon && (
                    <span className="expand-icon">
                        <Icon
                            onClick={() => (cellPops.onClick === undefined ? onExpand() : {})}
                            type={expanded ? ICON_TYPE_MINUS : ICON_TYPE_PLUS}
                        />
                    </span>
                )}
                {expandRender(render
                    ? render(data, row, index)
                    : (<span className="table-cell">{data}</span>))}
            </InnerComponent>
        </td>
    );
}

export default RowCell;
